import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  signal,
  inject,
  DestroyRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  Facility,
  FacilityWithLevels,
  FeatureFlag,
  PermissionsDTO,
  PermissionsMetadata,
  SavePermissionsPayload,
  UserInfo,
} from '../../user-management.model';
import { UserManagementService } from '../../../../services/user-management.service';
import { UserInfoComponent } from './user-info/user-info.component';
import { UserAccessControlComponent } from '../user-access-control/user-access-control.component';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { SkeletonModule } from 'primeng/skeleton';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-permissions-tab',
  imports: [UserInfoComponent, UserAccessControlComponent, SkeletonModule],
  templateUrl: './permissions-tab.component.html',
  styleUrl: './permissions-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsTabComponent implements OnInit {
  @ViewChild(UserAccessControlComponent) accessControl!: UserAccessControlComponent;

  userData = signal<UserInfo>({});
  allFeatureFlags = signal<FeatureFlag[]>([]);
  enabledFeatureFlags = signal<FeatureFlag[]>([]);
  userPermissions = signal<Facility[]>([]);
  productGroups = signal<FacilityWithLevels[]>([]);
  isLoading = signal<boolean>(true);
  isDirty = signal<boolean>(false);

  /** Injected by parent — called after a successful save so parent can re-fetch and push fresh data */
  onSaveSuccess: () => void = () => {};

  private userId = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly activeRoute: ActivatedRoute,
    private readonly userManagementService: UserManagementService,
    private readonly snackbarService: SnackbarV3Service,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userId = this.activeRoute.parent!.snapshot.paramMap.get('id')!;
  }

  onDirtyChange(dirty: boolean): void {
    this.isDirty.set(dirty);
  }

  /** Called by UserDetailsComponent when the Save button is clicked */
  save(): void {
    this.accessControl.triggerSave();
  }

  /** Called by parent (UserDetailsComponent) to push fetched data into this component */
  applyData(userInfo: UserInfo, permissions: PermissionsDTO, metadata: PermissionsMetadata): void {
    this.userData.set(userInfo);

    const allFlags = metadata.featureFlags ?? [];
    this.allFeatureFlags.set(allFlags);

    const userEnabledIds = new Set((permissions.featureFlags ?? []).map((f) => f.id));
    this.enabledFeatureFlags.set(allFlags.filter((f) => userEnabledIds.has(f.id)));

    const assignedMap = new Map<string, { Technical: number; Pricing: number }>();
    for (const fac of permissions.productPermissions ?? []) {
      for (const product of fac.products) {
        assignedMap.set(`${fac.facility}|${product.id}`, {
          Technical: product.maxLevels?.Technical ?? 0,
          Pricing: product.maxLevels?.Pricing ?? 0,
        });
      }
    }

    const mapped: FacilityWithLevels[] = (metadata.productGroups ?? []).map((fac) => ({
      facility: fac.facility,
      products: fac.products.map((product) => {
        const assigned = assignedMap.get(`${fac.facility}|${product.id}`);
        return { ...product, currentLevels: assigned ?? { Technical: 0, Pricing: 0 } };
      }),
    }));
    this.productGroups.set(mapped);

    this.isLoading.set(false);
    this.cdr.markForCheck();
  }

  /** Receives the payload from UserAccessControlComponent and calls the API */
  onSave(payload: SavePermissionsPayload): void {
    this.userManagementService
      .savePermissions(this.userId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbarService.add({ summary: 'Success', detail: 'Permissions saved successfully', severity: 'success' });
          this.isLoading.set(true);
          this.cdr.markForCheck();
          this.onSaveSuccess();
        },
      });
  }
}
